import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { Collapse } from "reactstrap";
import { alertsAPI } from "components/Apis/alerts";
import { formatDjangoDateTime } from "utils/dates";

const normalizeComments = (comments) => {
    if (Array.isArray(comments)) return comments;
    return [];
};

const AlertCard = ({ alert, onRefresh }) => {
    const targetType = alert?.target?.target_type || "unknown";
    const target = alert?.target?.target || {};
    const stops = Array.isArray(target.stops) ? target.stops : [];
    const nextStop =
        stops.find((stop) => stop?.status === "En Route") ||
        stops.find((stop) => stop?.status === "Not Started") ||
        stops[0] ||
        null;
    const cover = target?.cover || null;
    const rawLevel = (alert?.level || "info").toLowerCase();
    const resolvedLabel = alert?.is_resolved ? "Resolved" : "Unresolved";
    const [isCollapsed, setIsCollapsed] = useState(!!alert?.is_resolved);
    const [actionStatus, setActionStatus] = useState({
        assigning: false,
        resolving: false,
        error: null,
    });
    const [isLoadEventsOpen, setIsLoadEventsOpen] = useState(false);
    const [comments, setComments] = useState(() => normalizeComments(alert?.comments));
    const [commentInput, setCommentInput] = useState("");
    const [editingCommentId, setEditingCommentId] = useState(null);
    const [editingValue, setEditingValue] = useState("");
    const [commentStatus, setCommentStatus] = useState({
        loading: false,
        error: null,
    });
    const contextEntries = useMemo(() => {
        if (!alert?.context || typeof alert.context !== "object") return [];
        return Object.entries(alert.context)
            .filter(([_, value]) => value !== null && value !== undefined && value !== "")
            .map(([key, value]) => ({
                key,
                value: typeof value === "object" ? JSON.stringify(value) : String(value),
            }));
    }, [alert?.context]);

    const assignedUser =
        alert?.user ||
        alert?.assigned_to ||
        alert?.assigned_user ||
        null;
    const assignedName = typeof assignedUser === "string"
        ? assignedUser
        : assignedUser?.name || assignedUser?.username || assignedUser?.email || null;
    const loadEvents = Array.isArray(target?.load_events) ? target.load_events : [];

    const handleAssignToMe = async () => {
        if (actionStatus.assigning) return;
        setActionStatus((prev) => ({ ...prev, assigning: true, error: null }));
        try {
            await alertsAPI.assignToMe(alert.id);
            if (onRefresh) onRefresh();
        } catch (error) {
            setActionStatus((prev) => ({
                ...prev,
                assigning: false,
                error: error?.response?.data || "Failed to assign alert.",
            }));
            return;
        }
        setActionStatus((prev) => ({ ...prev, assigning: false }));
    };

    const handleResolve = async () => {
        if (actionStatus.resolving) return;
        setActionStatus((prev) => ({ ...prev, resolving: true, error: null }));
        try {
            await alertsAPI.resolve(alert.id);
            setIsCollapsed(true);
            await new Promise((resolveDelay) => setTimeout(resolveDelay, 220));
            if (onRefresh) onRefresh();
        } catch (error) {
            setActionStatus((prev) => ({
                ...prev,
                resolving: false,
                error: error?.response?.data || "Failed to resolve alert.",
            }));
            return;
        }
        setActionStatus((prev) => ({ ...prev, resolving: false }));
    };

    useEffect(() => {
        setComments(normalizeComments(alert?.comments));
    }, [alert?.id, alert?.comments]);

    useEffect(() => {
        if (alert?.is_resolved) {
            setIsCollapsed(true);
        }
    }, [alert?.is_resolved]);

    const handleAddComment = async () => {
        const trimmed = commentInput.trim();
        if (!trimmed || commentStatus.loading) return;
        setCommentStatus({ loading: true, error: null });
        try {
            const response = await alertsAPI.createComment(alert.id, trimmed);
            const created = response?.data;
            if (created?.id) {
                setComments((prev) => [...prev, created]);
                setCommentInput("");
            } else if (onRefresh) {
                onRefresh();
            }
            setCommentStatus({ loading: false, error: null });
        } catch (error) {
            setCommentStatus({
                loading: false,
                error: error?.response?.data || "Failed to add comment.",
            });
        }
    };

    const handleStartEditComment = (comment) => {
        setEditingCommentId(comment?.id || null);
        setEditingValue(comment?.comment || "");
    };

    const handleSaveEditComment = async () => {
        const trimmed = editingValue.trim();
        if (!editingCommentId || !trimmed || commentStatus.loading) return;
        setCommentStatus({ loading: true, error: null });
        try {
            const response = await alertsAPI.updateComment(editingCommentId, trimmed);
            const updated = response?.data;
            setComments((prev) => prev.map((comment) => (
                comment.id === editingCommentId
                    ? { ...comment, ...(updated || {}), comment: trimmed }
                    : comment
            )));
            setEditingCommentId(null);
            setEditingValue("");
            setCommentStatus({ loading: false, error: null });
        } catch (error) {
            setCommentStatus({
                loading: false,
                error: error?.response?.data || "Failed to update comment.",
            });
        }
    };

    const handleDeleteComment = async (commentId) => {
        if (!commentId || commentStatus.loading) return;
        setCommentStatus({ loading: true, error: null });
        try {
            await alertsAPI.deleteComment(commentId);
            setComments((prev) => prev.filter((comment) => comment.id !== commentId));
            if (editingCommentId === commentId) {
                setEditingCommentId(null);
                setEditingValue("");
            }
            setCommentStatus({ loading: false, error: null });
        } catch (error) {
            setCommentStatus({
                loading: false,
                error: error?.response?.data || "Failed to remove comment.",
            });
        }
    };

    const previewText = useMemo(() => {
        if (targetType === "stop") {
            const city = target.city || target.location_city || "";
            const state = target.state || target.location_state || "";
            const cityState = city && state ? `${city}, ${state}` : city || state;
            return target.location_name
                ? `${target.trip || "—"}\nStop #${target.seq ?? "—"} ${target.location_name}${cityState ? ` (${cityState})` : ""}`
                : null;
        }
        if (targetType === "order") {
            return `Order #${target.order_no || "—"} ${target.order_group || ""} ${target.backorder || ""}`.trim();
        }
        if (cover) return cover;
        if (nextStop) {
            return `Next Stop: ${nextStop.location_name} (${nextStop.city}, ${nextStop.state})`;
        }
        return null;
    }, [cover, nextStop, target, targetType]);
    const previewLines = useMemo(
        () => (previewText ? String(previewText).split("\n").filter(Boolean) : []),
        [previewText]
    );

    return (
        <article className={`alert-card ${isCollapsed ? "alert-card--collapsed" : ""}`}>
            <header className="alert-card__header">
                <div className="alert-card__title">
                    <strong>{alert.message}</strong>
                    <span className={`alert-badge alert-badge--${rawLevel}`}>
                        {alert?.level || "Info"}
                    </span>
                </div>
                <div className="alert-card__header-actions">
                    {previewText && (
                        <div className="alert-card__header-target">
                            {previewLines.map((line, index) => (
                                <span
                                    key={`${alert.id}-preview-${index}`}
                                    className={index === 0 ? "alert-card__header-target-tip" : undefined}
                                >
                                    {line}
                                </span>
                            ))}
                        </div>
                    )}
                    <button
                        className="alert-card__toggle"
                        type="button"
                        onClick={() => setIsCollapsed((prev) => !prev)}
                        aria-label={isCollapsed ? "Expand alert details" : "Collapse alert details"}
                        title={isCollapsed ? "Show details" : "Hide details"}
                    >
                        <i className={`fa ${isCollapsed ? "fa-chevron-down" : "fa-chevron-up"}`} />
                    </button>
                </div>
            </header>
            <div className="alert-card__meta">
                {contextEntries.length > 0 && (
                    <div className="alert-card__context alert-card__context-section">
                        <div className="alert-card__section-heading">Context</div>
                        <div className="alert-card__target-grid">
                            {contextEntries.map((entry) => (
                                <div key={`${alert.id}-context-${entry.key}`}>
                                    {entry.key.replace(/_/g, " ")}: {entry.value}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            <hr className="alert-card__separator alert-card__separator--inner" />
            <Collapse isOpen={!isCollapsed} className="alert-card__collapse">
                <>
                    {targetType === "stop" ? (
                        <div className="alert-card__target alert-card__target--stop">
                            <div className="alert-card__target-grid">
                                <div>Status: {target.status || "—"}</div>
                                <div>Scheduled: {target.scheduled_arrival_time || "—"}</div>
                                {target.arrival_time ? (
                                    <div>Arrived: {target.arrival_time}</div>
                                ) : target.estimated_arrival_time ? (
                                    <div>ETA: {target.estimated_arrival_time}</div>
                                ) : null}
                                {target.departure_time && <div>Departed: {target.departure_time}</div>}
                                {target.distance_mi !== null && target.distance_mi !== undefined && (
                                    <div>Distance: {target.distance_mi} mi</div>
                                )}
                                <div className="alert-card__events-header">
                                    <span className="alert-card__events-title">
                                        Load Events <span className="alert-card__events-count">({loadEvents.length})</span>
                                    </span>
                                    <button
                                        type="button"
                                        className="alert-card__events-toggle"
                                        onClick={() => setIsLoadEventsOpen((prev) => !prev)}
                                        disabled={loadEvents.length === 0}
                                        aria-expanded={isLoadEventsOpen}
                                        aria-label="Toggle load events"
                                    >
                                        <i className={`fa ${isLoadEventsOpen ? "fa-chevron-up" : "fa-chevron-down"}`} />
                                    </button>
                                </div>
                                <Collapse isOpen={isLoadEventsOpen}>
                                    <div className="alert-card__events-list">
                                        {loadEvents.length > 0 ? (
                                            loadEvents.map((event, index) => (
                                                <div key={`${alert.id}-event-${index}`} className="alert-card__event-item">
                                                    <span className="alert-card__event-type">
                                                        {(event?.type || "event").toUpperCase()}
                                                    </span>
                                                    <span className="alert-card__event-order">
                                                        Order #{event?.order?.order_no || "—"}{" "}
                                                        {event?.order?.order_group || ""} {event?.order?.backorder || ""}
                                                    </span>
                                                    <span className="alert-card__event-meta-item">
                                                        Units <strong>{event?.order?.units ?? "—"}</strong>
                                                    </span>
                                                    <span className="alert-card__event-meta-item">
                                                        Pieces <strong>{event?.pieces ?? "—"}</strong>
                                                    </span>
                                                    <span className="alert-card__event-meta-item">
                                                        Weight <strong>{event?.weight ?? "—"}</strong>
                                                    </span>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="alert-card__event-item">No load events.</div>
                                        )}
                                    </div>
                                </Collapse>
                            </div>
                        </div>
                    ) : targetType === "order" ? (
                        <div className="alert-card__target alert-card__target--order">
                            <div className="alert-card__target-grid">
                                <div className="alert-card__target-title">Order Details</div>
                                {target.units && <div>Units: {target.units}</div>}
                                {target.pickup_at && <div>Pickup: {target.pickup_at} {target.pickup_date}</div>}
                                {target.deliver_to && <div>Drop: {target.deliver_to} {target.delivery_date}</div>}
                            </div>
                        </div>
                    ) : (
                        <div className="alert-card__target alert-card__target--trip">
                            <div className="alert-card__target-grid">
                                <div className="alert-card__target-title">Trip Details</div>
                                {target.status && <div>Status: {target.status}</div>}
                            </div>
                        </div>
                    )}
                    <hr className="alert-card__separator alert-card__separator--inner" />
                    <div className="alert-comment">
                        <div className="alert-comment__title">Comments</div>
                        {comments.length > 0 && (
                            <div className="alert-comment__list">
                                {comments.map((comment) => {
                                    const commentId = comment?.id;
                                    const isEditing = editingCommentId === commentId;
                                    return (
                                        <div key={`${alert.id}-comment-${commentId}`} className="alert-comment__item">
                                            <div className="alert-comment__item-meta">
                                                <span>{comment?.user || "Unknown user"}</span>
                                                <span>{comment?.created_at ? formatDjangoDateTime(comment.created_at) : ""}</span>
                                            </div>
                                            {isEditing ? (
                                                <input
                                                    className="alert-comment__input alert-comment__input--inline"
                                                    type="text"
                                                    value={editingValue}
                                                    onChange={(event) => setEditingValue(event.target.value)}
                                                    onBlur={handleSaveEditComment}
                                                    autoFocus
                                                />
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="alert-comment__item-text-button"
                                                    onClick={() => handleStartEditComment(comment)}
                                                    title={comment?.comment || ""}
                                                >
                                                    <span className="alert-comment__item-text">{comment?.comment || ""}</span>
                                                </button>
                                            )}
                                            <div className="alert-comment__item-actions">
                                                {isEditing ? (
                                                    <>
                                                        <button
                                                            className="alert-action-button"
                                                            type="button"
                                                            onClick={() => {
                                                                setEditingCommentId(null);
                                                                setEditingValue("");
                                                            }}
                                                            disabled={commentStatus.loading}
                                                        >
                                                            Cancel
                                                        </button>
                                                    </>
                                                ) : (
                                                    <button
                                                        className="alert-action-button alert-action-button--danger"
                                                        type="button"
                                                        onClick={() => handleDeleteComment(commentId)}
                                                        disabled={commentStatus.loading}
                                                        aria-label="Delete comment"
                                                        title="Delete comment"
                                                    >
                                                        <i className="fa fa-trash" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        {comments.length === 0 && (
                            <div className="alert-comment__empty">No comments yet.</div>
                        )}
                        <div className="alert-comment__form alert-comment__item">
                            <label className="alert-comment__label" htmlFor={`add-comment-${alert.id}`}>
                                Add Comment
                            </label>
                            <input
                                id={`add-comment-${alert.id}`}
                                className="alert-comment__input alert-comment__input--compose"
                                type="text"
                                value={commentInput}
                                onChange={(event) => setCommentInput(event.target.value)}
                                placeholder="Add a note for this alert"
                            />
                            <div className="alert-comment__actions">
                                <button
                                    className="alert-action-button"
                                    type="button"
                                    onClick={handleAddComment}
                                    disabled={commentStatus.loading || !commentInput.trim()}
                                >
                                    Save
                                </button>
                                <button
                                    className="alert-action-button"
                                    type="button"
                                    onClick={() => setCommentInput("")}
                                    disabled={commentStatus.loading || !commentInput}
                                >
                                    Cancel
                                </button>
                                {commentStatus.loading && (
                                    <span className="alert-comment__saving">Saving...</span>
                                )}
                                {commentStatus.error && (
                                    <span className="alert-comment__error">
                                        {typeof commentStatus.error === "string" ? commentStatus.error : "Comment action failed."}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </>
            </Collapse>
            {!isCollapsed && <hr className="alert-card__separator" />}
            <footer className="alert-card__footer">
                <div className="alert-card__footer-main">
                    <div className="alert-card__actions alert-card__footer-actions">
                        <button
                            className="alert-action-button alert-action-button--danger"
                            type="button"
                            onClick={handleResolve}
                            disabled={actionStatus.resolving || alert?.is_resolved}
                        >
                            {alert?.is_resolved ? "Resolved" : actionStatus.resolving ? "Resolving..." : "Resolve"}
                        </button>
                        <button
                            className="alert-action-button"
                            type="button"
                            onClick={handleAssignToMe}
                            disabled={actionStatus.assigning}
                        >
                            {actionStatus.assigning ? "Assigning..." : "Assign to me"}
                        </button>
                    </div>
                    <div className="alert-card__footer-meta">
                        <div className="alert-card__footer-item">
                            <span className="alert-card__footer-label">Alert Status</span>
                            <span className="alert-badge">{resolvedLabel}</span>
                        </div>
                        <div className="alert-card__footer-item">
                            <span className="alert-card__footer-label">Created</span>
                            <span>{alert?.created_at || "—"}</span>
                        </div>
                        <div className="alert-card__footer-item">
                            <span className="alert-card__footer-label">Assigned</span>
                            <span>{assignedName || "Unassigned"}</span>
                        </div>
                    </div>
                </div>
                {actionStatus.error && (
                    <span className="alert-action-error">
                        {typeof actionStatus.error === "string"
                            ? actionStatus.error
                            : "Action failed."}
                    </span>
                )}
            </footer>
        </article>
    );
};

AlertCard.propTypes = {
    alert: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        message: PropTypes.string,
        level: PropTypes.string,
        is_resolved: PropTypes.bool,
        created_at: PropTypes.string,
        user: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({
                name: PropTypes.string,
                username: PropTypes.string,
                email: PropTypes.string,
            }),
        ]),
        assigned_to: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({
                name: PropTypes.string,
                username: PropTypes.string,
                email: PropTypes.string,
            }),
        ]),
        assigned_user: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({
                name: PropTypes.string,
                username: PropTypes.string,
                email: PropTypes.string,
            }),
        ]),
        target: PropTypes.shape({
            cover_type: PropTypes.string,
            target: PropTypes.shape({
                cover: PropTypes.string,
                cover_type: PropTypes.string,
                source: PropTypes.string,
                status: PropTypes.string,
                external_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                distance_mi: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                carrier: PropTypes.shape({
                    name: PropTypes.string,
                    phone_no: PropTypes.string,
                    trailer_id: PropTypes.string,
                    tracking_no: PropTypes.string,
                }),
                vehicle: PropTypes.shape({
                    unit_no: PropTypes.string,
                    plate_no: PropTypes.string,
                }),
                trailer: PropTypes.shape({
                    trailer_no: PropTypes.string,
                }),
                drivers: PropTypes.arrayOf(
                    PropTypes.shape({
                        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                        name: PropTypes.string,
                    })
                ),
                stops: PropTypes.arrayOf(
                    PropTypes.shape({
                        location_name: PropTypes.string,
                        city: PropTypes.string,
                        state: PropTypes.string,
                    })
                ),
            }),
        }),
    }).isRequired,
    onRefresh: PropTypes.func,
};

export default AlertCard;
